import { Injectable } from '@angular/core';
import { GlobalsService } from './globals.service';
import { GlobalsService as GlobalsServiceMicro } from '@app/services/micro/globals.service';
import { Passenger } from '@app/models/micro/passenger.model';
import { Reservation } from '@app/models/micro/reservation.model';
import { LocalDataService } from '../local-data.service';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import {
  ESTATUS_CHECKIN,
  MISSING_CHECKIN_REQUIRED_FIELDS,
  PAX_TYPE,
  ResultModalRedeem,
  SeatPassangerEstatus,
  StatusBag,
  TypeBagsWeight,
} from '@app/enums';
import { ApiService } from './api.service';
import { CountriesInt, PassportScanInfoInt } from '@app/interfaces';
import { PassportInfoInt } from '@app/models/micro/updateDocuments.model';
import { MissingCheckinRequiredFields } from '@app/models/micro/response/viewDocuments.model';
import { MessagesService } from '../messages.service';
import { TypesErrors } from '@app/enums/errors.enum';
import { messageParamsGeneric, messageParamsUpdateDocuments } from '@app/utils';
import { BehaviorSubject } from 'rxjs';
import { ErrorModel } from '@models/micro/error.model';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BagsRedemptionModalComponent } from '@app/shared/modals/bags-redemption-modal/bags-redemption-modal.component';
import {
  BagsBenefitModalComponent,
  CobrandConfig,
  TierBenefitConfig,
} from '@app/shared/modals/bags-benefit-modal/bags-benefit-modal.component';
import {
  BagsBenefitErrorModalComponent,
  BENEFIT_ERROR_RETRY,
} from '@app/shared/modals/bags-benefit-error-modal/bags-benefit-error-modal.component';
import cobrandCatalog from '@assets/data/cobrand-catalog.json';
import { classCssTiers } from '@app/utils/constants/redentions';
import { ApiAncillaries } from '@app/services/micro/ancillaries.service';

/**
 * Servicio encargado de la gestión de pasajeros en una reserva.
 * Proporciona métodos para actualizar información de pasajeros,
 * manejar selecciones y verificar documentos requeridos para el check-in.
 */
@Injectable({
  providedIn: 'root',
})
export class PassengerService {
  //selectedPassengersToBags: Passenger[] = [];
  /**
   * Lista de pasajeros seleccionados.
   */

  passengersSelections: Passenger[];
  totalPassengersSelectNoDocs: number = 1;
  paxStepNac: number = 0;
  private currentPassenger: Passenger;
  private paxStepIntSubject = new BehaviorSubject<number>(1);
  paxStepInt$ = this.paxStepIntSubject.asObservable();

  constructor(
    private _globalService: GlobalsService,
    private _globalServiceMicro: GlobalsServiceMicro,
    private _message: MessagesService,
    private _modalService: NgbModal,
    private _ancillaries: ApiAncillaries
  ) {}
  
  getCurrentPassengerToPay() {
    const passengerSelected = this.getPassengerSelection();
    let currentPassenger = passengerSelected.filter((x) =>
      x.bagStack.find(
        (bag) =>
          bag.selected &&
          !bag.used &&
          bag.eligibility &&
          bag.rficSubcode !== '07X' &&
          bag.typeBagWeight !== TypeBagsWeight._10KG // Excluir 10kg
      )
    )?.[0];

    if (!currentPassenger) {
      currentPassenger = passengerSelected.filter((x) =>
        x.bagStack.find(
          (bag) =>
            bag.selected &&
            !bag.used &&
            bag.rficSubcode !== '07X' && // Excluir carryon checkbox
            bag.typeBagWeight !== TypeBagsWeight._10KG // Excluir 10kg
        )
      )?.[0];
    }
    return currentPassenger;
  }

  /**
   * Actualiza la selección de pasajeros con base en la reserva.
   * @param lookUp Lista de reservas a evaluar.
   */

  updateSelectionsPassenger(lookUp: Reservation[]) {
    const reservationSelection = this._globalService.getReservation();
    const reservarionUpdate = lookUp.find(
      (reservation) =>
        reservation.flightSummary.arrivalAirport ==
          reservationSelection.flightSummary.arrivalAirport &&
        reservation.flightSummary.departureAirport ==
          reservationSelection.flightSummary.departureAirport
    );
    this._globalService.setPNRLookUp(lookUp);
    this._globalService.setReservation(reservarionUpdate);
    this.setPassengerSelection(this.passengersSelections);
  }

  /**
   * Actualiza la información de un pasajero seleccionado.
   * @param property Propiedad a modificar.
   * @param value Nuevo valor.
   */

  updatePassengerInfo(property: any, value: any) {
    if (this.currentPassenger) {
      this.currentPassenger.passengerInfo[property] = value;
    }
    this._globalService.setPassenger(this.currentPassenger);
  }

  /**
   * Obtiene el tipo de pasajero en formato abreviado.
   * @returns String representando el tipo de pasajero.
   */

  getPaxType() {
    let paxType = '';
    if (this.currentPassenger?.paxType == PAX_TYPE.ADULT) {
      paxType = 'ADT';
    } else if (this.currentPassenger?.paxType == PAX_TYPE.CHILD) {
      paxType = 'CHD';
    } else if (this.currentPassenger?.paxType == PAX_TYPE.INFANT) {
      paxType = 'INF';
    }
    return paxType;
  }

  /**
   * Obtiene la posición actual del pasajero en la selección.
   * @returns Índice del pasajero en la selección.
   */

  getPositionCurrentPassenger() {
    const positionPax = sessionStorage.getItem('positionCurrentPassenger');
    return positionPax ? JSON.parse(positionPax) : undefined;
  }

  /**
   * Guarda la posición actual del pasajero en la sesión.
   * @param position Posición del pasajero en la lista.
   */

  setPositionCurrentPassenger(position) {
    sessionStorage.setItem('positionCurrentPassenger', JSON.stringify(position));
  }

  /**
   * Retorna el pasajero actualmente seleccionado.
   * @returns Objeto del pasajero seleccionado.
   */

  getCurrentPassenger() {
    const position = this.getPositionCurrentPassenger();
    const passagers = this.getPassengerSelection();
    return passagers[position];
  }

  /**
   * Establece la lista de pasajeros seleccionados para check-in.
   * @param passengers Lista de pasajeros a marcar como seleccionados.
   */

  setPassengerSelection(passengers: Array<Passenger>): void {
    const allPassengers = this._globalService.getPassengers();
    this.passengersSelections = [];
    for (let passenger of allPassengers) {
      passenger.checkinSelected = false;
      if (passengers.find((x) => x.id == passenger.id)) {
        passenger.checkinSelected = true;
        this.passengersSelections.push(passenger);
      }
      this._globalService.setPassenger(passenger);
    }
  }

  /**
   * Obtiene los pasajeros seleccionados, opcionalmente incluyendo infantes.
   * @param includeInfant Si se incluyen infantes en la lista.
   * @returns Lista de pasajeros seleccionados.
   */

  getPassengerSelection(includeInfant = false): Array<Passenger> {
    const allPassengers = this._globalServiceMicro.getPassengers();
    const selected = includeInfant
      ? allPassengers.filter((x) => x.checkinSelected)
      : allPassengers.filter((x) => x.checkinSelected && x.paxType !== PAX_TYPE.INFANT);

    return selected.filter((passenger, index, array) => {
      return (
        index ===
        array.findIndex((item) => {
          const sameId = passenger.id != null && item.id != null && item.id === passenger.id;
          const sameNr =
            !!passenger.nameRefNumber?.trim() &&
            item.nameRefNumber?.trim() === passenger.nameRefNumber?.trim();
          const samePid =
            passenger.passengerId != null &&
            item.passengerId != null &&
            item.passengerId === passenger.passengerId;
          return sameId || sameNr || samePid;
        })
      );
    });
  }

  setSelectCurrentPassenger(passenger: Passenger): void {
    this.currentPassenger = passenger;
  }

  getSelectCurrentPassenger() {
    return this.currentPassenger;
  }

  get findPaxSkyPriority() {
    return !!this._globalService.getPassengers().find((pax) => pax.isSkyPriority);
  }

  setSelectPassengersToBags(passengers: Passenger[]) {
    //this.selectedPassengersToBags = passengers
    this.getPassengerSelection().map((p) => {
      passengers.map((psg) => {
        if (p.id === psg.id) {
          p.isSelectForBagagge = true;
          this._globalServiceMicro.setPassenger(p);
        }
      });
    });
  }

  getSelectPassengersToBags() {
    return this.getPassengerSelection().filter((p) => p.isSelectForBagagge) || [];
  }

  getSelectPassengersToSeats() {
    return (
      this.getPassengerSelection().filter((p) =>
        p?.seatsNew.some((s) => !s?.paidSeat || s?.status == SeatPassangerEstatus.PENDING_PAYMENT)
      ) || []
    );
  }

  get passengersToBags() {
    return this._globalService
      .getPassengers()
      .filter((p) => p.checkinSelected && p.paxType != PAX_TYPE.INFANT && p.isSelectForBagagge);
  }
  hasPassengersVerifyTravelDocuments() {
    const passengers = this.getPassengerSelection().find((p) => p.hasVerifyDocument);
    return !!passengers;
  }

  /**
   * Mapea la información escaneada del pasaporte a la estructura del modelo de pasaporte.
   * @param passportInfo Información escaneada del pasaporte.
   * @returns Objeto con la información formateada del pasaporte.
   */

  /**
   * Mapea la información escaneada del pasaporte al modelo de documento de pasaporte.
   * Incluye los campos requeridos por CBP: firstName, lastName, gender, birthDate.
   * @param passportInfo Información del pasaporte escaneado
   * @returns Documento de pasaporte mapeado con todos los campos
   */
  mapPassportInfoToPassportDocument(passportInfo: PassportScanInfoInt): PassportInfoInt {
    // CI-17097: Mapeo de campos adicionales desde escaneo de pasaporte
    // Incluye: firstName, lastName, gender, birthDate
    let passportDocument: PassportInfoInt = {
      firstName: passportInfo.firstName,
      lastName: passportInfo.lastName,
      gender: this.normalizeGender(passportInfo.gender),
      birthDate: passportInfo.dob,
      documentNumber: passportInfo.number,
      issuingCountry: passportInfo.issuingCountry,
      nationality: passportInfo.country,
      passportScanned: true,
      expirationDate: `${
        passportInfo.expirationDateYear
      }-${passportInfo?.expirationDateMonth?.padStart(
        2,
        '0'
      )}-${passportInfo?.expirationDateDay?.padStart(2, '0')}`,
    };

    return passportDocument;
  }

  /**
   * Construye los campos obligatorios faltantes para el check-in de un pasajero.
   * @param currentPassenger Pasajero actual.
   * @param passengerDoc Información del pasajero.
   * @param response Respuesta del servicio con datos de pasajeros.
   */

  buildMissingCheckinRequiredFields(currentPassanger: Passenger, passegerdoc, response) {
    const omitMissingCheckinRequiredFields = [
      'PASSENGER_TRACING_CONTACT_PHONE',
      'PASSENGER_TRACING_CONTACT_EMAIL',
      'COUNTRY_OF_RESIDENCE',
      'COUNTRY_OF_RESIDENCE/INF',
      'EMAIL',
      'PHONE',
      'IGPP',
      'TIM',
      'ESTA',
      'ETA',
      'DHS',
    ];
    const passengerINF = this._globalService
      .getPassengers()
      .find((p) => p.nameRefNumber == currentPassanger.passengerInfant);
    currentPassanger.missingCheckinRequiredFields = [];
    if (currentPassanger.passengerInfo.passport) {
      for (const doc of passegerdoc.missingCheckinRequiredFields) {
        const hasTimaticInfo = !!currentPassanger.passengerInfo?.timaticRQ?.returnDate;
        const finDocument = !!currentPassanger.missingCheckinRequiredFields.find(
          (docu) => docu.document === doc.document
        );
        const isReturnDateDoc = doc.document?.toUpperCase().includes('RETURN_DATE');
        if (
          !omitMissingCheckinRequiredFields.includes(doc.document) &&
          !finDocument &&
          !(hasTimaticInfo && isReturnDateDoc)
        ) {
          currentPassanger.missingCheckinRequiredFields.push(doc);
        }
      }
    }
    if (currentPassanger.passengerInfant) {
      const passengerINF = response.passengers.find(
        (p) => p.passengerNameNumber == currentPassanger.passengerInfant
      );
      const hasTIMINF = passengerINF?.missingCheckinRequiredFields?.some(
        (item) => item.document === 'TIM/INF'
      );
      if (!hasTIMINF) {
        for (const doc of passengerINF.missingCheckinRequiredFields?.filter(
          (doc) =>
            doc.document.includes('INF') &&
            !omitMissingCheckinRequiredFields.includes(doc.document) &&
            !(
              doc.document === 'DESTINATION_ADDRESS/INF' &&
              !passengerINF.passengerInfo?.passport?.documentNumber
            )
        ) || []) {
          const validatePassportInf = currentPassanger.missingCheckinRequiredFields.find(
            (docu) => docu.document === 'PASSPORT/INF'
          );
          if (validatePassportInf == undefined) {
            currentPassanger.missingCheckinRequiredFields.push(doc);
          }
        }
      }
    } else if (currentPassanger.paxType == PAX_TYPE.INFANT) {
      const passengerADT = this._globalService
        .getPassengers()
        .find((p) => p.passengerInfant == currentPassanger.nameRefNumber);
      const hasTIMINF = passengerADT?.missingCheckinRequiredFields?.some(
        (item) => item.document.includes('INF') && item.document !== 'PASSPORT/INF'
      );

      if (!hasTIMINF && currentPassanger.passengerInfo.passport) {
        for (const doc of passegerdoc.missingCheckinRequiredFields.filter(
          (doc) =>
            !doc.document.includes('PERMANENT_RESIDENT_CARD') &&
            !omitMissingCheckinRequiredFields.includes(doc.document)
        )) {
          const validatePassportInf = currentPassanger.missingCheckinRequiredFields.find(
            (docu) => docu.document === 'PASSPORT/INF'
          );
          if (validatePassportInf == undefined) {
            passengerADT.missingCheckinRequiredFields.push(doc);
          }
        }
      }

      if (
        currentPassanger.missingCheckinRequiredFields.find(
          (x) => x.document === MISSING_CHECKIN_REQUIRED_FIELDS.PASSPORT_INF
        ) === undefined
      ) {
        const passportInf = passengerADT.missingCheckinRequiredFields.find(
          (doc) => doc.document == 'PASSPORT/INF'
        );
        if (passportInf) {
          passportInf.status = !!currentPassanger.passengerInfo.passport?.passportScanned;
          passportInf.save = !!currentPassanger.passengerInfo.passport?.documentNumber;
        }
      }
      if (
        currentPassanger.missingCheckinRequiredFields.find(
          (x) => x.document === MISSING_CHECKIN_REQUIRED_FIELDS.DESTINATION_ADDRESS_INF
        ) === undefined
      ) {
        const document = passengerADT.missingCheckinRequiredFields.find(
          (doc) => doc.document == 'DESTINATION_ADDRESS/INF'
        );
        if (document) {
          const save = !!currentPassanger.passengerInfo.destinationAddress?.city;
          document.status = save;
          document.save = save;
        }
      }
    }

    let newData: MissingCheckinRequiredFields;
    if (
      currentPassanger.missingCheckinRequiredFields.find(
        (x) => x.document === MISSING_CHECKIN_REQUIRED_FIELDS.PASSPORT
      ) === undefined
    ) {
      newData = new MissingCheckinRequiredFields(MISSING_CHECKIN_REQUIRED_FIELDS.PASSPORT);
      newData.preview = 'Required';
      newData.isRequired = true;
      newData.title = 'documents.passport-text';
      newData.status = !!currentPassanger.passengerInfo.passport?.passportScanned;
      newData.save = !!currentPassanger.passengerInfo.passport?.documentNumber;
      currentPassanger.missingCheckinRequiredFields.unshift(newData);

      if (
        currentPassanger.passengerInfant &&
        currentPassanger.missingCheckinRequiredFields.find(
          (x) => x.document === MISSING_CHECKIN_REQUIRED_FIELDS.PASSPORT_INF
        ) === undefined
      ) {
        newData = new MissingCheckinRequiredFields(MISSING_CHECKIN_REQUIRED_FIELDS.PASSPORT_INF);
        newData.preview = 'Required';
        newData.isRequired = true;
        newData.title = 'documents.passport-text';
        newData.status = !!currentPassanger.passengerInfo.passport?.passportScanned;
        newData.save = !!passengerINF.passengerInfo.passport?.documentNumber;
        currentPassanger.missingCheckinRequiredFields.unshift(newData);
      }
    }
    if (
      currentPassanger.missingCheckinRequiredFields.find(
        (x) =>
          x.document === MISSING_CHECKIN_REQUIRED_FIELDS.VISA ||
          x.document === MISSING_CHECKIN_REQUIRED_FIELDS.VISA_US
      ) === undefined &&
      currentPassanger.passengerInfo.visa &&
      currentPassanger.paxType !== PAX_TYPE.INFANT
    ) {
      const countryCode = response.passengers.find(
        (p) => p.passengerNameNumber == currentPassanger.nameRefNumber
      );
      const aplicableCode = countryCode?.timaticInfoList
        ? countryCode?.timaticInfoList[0]?.country?.countryCode
        : undefined;
      currentPassanger.applicationVisaContry = aplicableCode;
      newData = new MissingCheckinRequiredFields(MISSING_CHECKIN_REQUIRED_FIELDS.VISA);
      newData.preview = 'Required';
      newData.isRequired = true;
      newData.title = 'paxForms.visa.visa_name';
      newData.status = true;
      newData.save = true;
      currentPassanger.missingCheckinRequiredFields.unshift(newData);
    }

    if (
      currentPassanger.passengerInfant &&
      response.passengers
        .find((p) => p.passengerNameNumber == currentPassanger.passengerInfant)
        ?.missingCheckinRequiredFields.find((d) => d.document.includes('VISA')) === undefined &&
      passengerINF?.passengerInfo?.visa
    ) {
      const countryCode = response.passengers.find(
        (p) => p.passengerNameNumber == currentPassanger.nameRefNumber
      );
      const aplicableCode = countryCode?.timaticInfoList
        ? countryCode?.timaticInfoList[0]?.country?.countryCode
        : undefined;
      currentPassanger.applicationVisaContry = aplicableCode;
      newData = new MissingCheckinRequiredFields(MISSING_CHECKIN_REQUIRED_FIELDS.VISA_INF);
      newData.title = 'paxForms.visa.visa_name';
      newData.status = true;
      newData.save = true;
      currentPassanger.missingCheckinRequiredFields.unshift(newData);
    }

    if (
      currentPassanger.missingCheckinRequiredFields.find(
        (x) => x.document === MISSING_CHECKIN_REQUIRED_FIELDS.DESTINATION_ADDRESS
      ) === undefined &&
      currentPassanger.passengerInfo.destinationAddress &&
      currentPassanger.paxType !== PAX_TYPE.INFANT
    ) {
      newData = new MissingCheckinRequiredFields(
        MISSING_CHECKIN_REQUIRED_FIELDS.DESTINATION_ADDRESS
      );
      newData.preview = 'Required';
      newData.isRequired = true;
      newData.title = 'documents.desAddrs-text';
      newData.status = true;
      newData.save = true;
      currentPassanger.missingCheckinRequiredFields.unshift(newData);
    }

    if (
      currentPassanger.passengerInfant &&
      response.passengers
        .find((p) => p.passengerNameNumber == currentPassanger.passengerInfant)
        ?.missingCheckinRequiredFields.find((d) => d.document.includes('DESTINATION_ADDRESS')) ===
        undefined &&
      passengerINF?.passengerInfo?.destinationAddress
    ) {
      newData = new MissingCheckinRequiredFields(
        MISSING_CHECKIN_REQUIRED_FIELDS.DESTINATION_ADDRESS_INF
      );
      newData.preview = 'Required';
      newData.isRequired = true;
      newData.title = 'documents.desAddrs-text';
      newData.status = true;
      newData.save = true;
      currentPassanger.missingCheckinRequiredFields.unshift(newData);
    }

    if (
      currentPassanger.missingCheckinRequiredFields.find(
        (x) => x.document === MISSING_CHECKIN_REQUIRED_FIELDS.RETURN_DATE
      ) === undefined &&
      currentPassanger.passengerInfo?.timaticRQ?.stayType &&
      currentPassanger.paxType !== PAX_TYPE.INFANT
    ) {
      const totalDoc = currentPassanger?.missingCheckinRequiredFields?.filter(
        (doc) => doc.document === 'RETURN_DATE_CO'
      )?.length;
      newData = new MissingCheckinRequiredFields(MISSING_CHECKIN_REQUIRED_FIELDS.RETURN_DATE);
      newData.preview = 'Required';
      newData.isRequired = true;
      newData.title = 'documents.tim-text';
      newData.status = true;
      newData.save = true;
      if (totalDoc < 1) {
        currentPassanger.missingCheckinRequiredFields.unshift(newData);
      } else {
        const index = currentPassanger?.missingCheckinRequiredFields?.findIndex(
          (doc) => doc.document === 'RETURN_DATE_CO'
        );
        currentPassanger.missingCheckinRequiredFields[index] = newData;
      }
    }

    if (
      currentPassanger.passengerInfant &&
      response.passengers.find(
        (p) =>
          p.passengerNameNumber?.trim() === currentPassanger.passengerInfant?.trim() &&
          !p.missingCheckinRequiredFields?.some((d) => d.document?.startsWith('RETURN_DATE')) &&
          p.timaticRQ?.returnDate
      )
    ) {
      newData = new MissingCheckinRequiredFields(MISSING_CHECKIN_REQUIRED_FIELDS.RETURN_DATE_INF);
      newData.preview = 'Required';
      newData.isRequired = true;
      newData.title = 'documents.tim-text';
      newData.status = true;
      newData.save = true;
      currentPassanger.missingCheckinRequiredFields.unshift(newData);
    }

    if (
      currentPassanger.missingCheckinRequiredFields.find(
        (x) => x.document === MISSING_CHECKIN_REQUIRED_FIELDS.EMERGENCY_CONTACT
      ) === undefined &&
      currentPassanger.passengerInfo.emergencyContact
    ) {
      newData = new MissingCheckinRequiredFields(MISSING_CHECKIN_REQUIRED_FIELDS.EMERGENCY_CONTACT);
      newData.preview = 'Required';
      newData.isRequired = true;
      newData.title = 'contactEmergency.emergency';
      newData.status = true;
      newData.save = true;
      currentPassanger.missingCheckinRequiredFields.unshift(newData);
    }

    if (
      currentPassanger.missingCheckinRequiredFields.find((x) =>
        x.document.includes('PERMANENT_RESIDENT_CARD')
      ) === undefined &&
      currentPassanger.passengerInfo.permanentResidentCard &&
      currentPassanger.paxType !== PAX_TYPE.INFANT
    ) {
      newData = new MissingCheckinRequiredFields(
        MISSING_CHECKIN_REQUIRED_FIELDS.PERMANENT_RESIDENT_CARD
      );
      newData.preview = 'Required';
      newData.isRequired = true;
      newData.title = 'recidivism.recidivism';
      newData.status = true;
      newData.save = true;
      currentPassanger.missingCheckinRequiredFields.unshift(newData);
    }

    const visa = currentPassanger.missingCheckinRequiredFields.find((el) =>
      el.document.includes('VISA')
    );
    if (visa && currentPassanger.applicationVisaContry == null) {
      const [docto, applicationVisaContry] = visa.document.split('_');
      currentPassanger.applicationVisaContry = applicationVisaContry;
      visa.document = docto;
    }

    const permanentResidentCard = currentPassanger.missingCheckinRequiredFields.find((d) =>
      d.document.includes('PERMANENT_RESIDENT_CARD')
    );
    if (permanentResidentCard && currentPassanger.passengerInfo.permanentResidentCard == null) {
      permanentResidentCard.document = 'PERMANENT_RESIDENT_CARD';
    }

    //ContactTracing
    if (
      passegerdoc.missingCheckinRequiredFields.find((doc) =>
        doc.document.includes('PASSENGER_TRACING_CONTACT')
      )
    ) {
      currentPassanger.requiredContactTracing = true;
    }

    currentPassanger.changeStatus();
  }

  showMessageUpdateDocuments() {
    const error = new ErrorModel().withCode('99').withMessage('checkin.update-documents');
    return this._message.error(
      {
        typesError: TypesErrors.UPDATE_DOCUMENTS,
        error,
      },
      null,
      TypesErrors.UPDATE_DOCUMENTS,
      messageParamsUpdateDocuments
    );
  }

  next() {
    const passengersSelection = this.getPassengerSelection();

    for (const pax of passengersSelection) {
      pax.changeStatus();
    }

    const current = this.getCurrentPassenger();
    const passengers = this.getPassengerSelection().filter(
      (pax) => pax.estatusCheckin == ESTATUS_CHECKIN.NOT_DOCUMENTS || !pax.checkinStatusDocuments
    );

    if (passengers.length > 0) {
      if (current) {
        const indxCurrent = passengers.findIndex(
          (pax) => pax.nameRefNumber === current.nameRefNumber
        );

        if (indxCurrent !== -1 && passengers.length > 1 && indxCurrent < passengers.length - 1) {
          const passenger = passengers[indxCurrent + 1];
          this.setPositionCurrentPassenger(indxCurrent + 1);
          this.setSelectCurrentPassenger(passenger);

          return passenger;
        }

        this.setSelectCurrentPassenger(passengers[0]);
        return passengers[0];
      }
      this.setSelectCurrentPassenger(passengers[0]);
      return passengers[0];
    }

    this.setSelectCurrentPassenger(undefined);
    return null;
  }

  incrementPaxStepInt() {
    const current = this.paxStepIntSubject.getValue();
    this.paxStepIntSubject.next(current + 1);
  }

  clearPaxStepInt() {
    this.paxStepIntSubject.next(1);
  }

  prev() {
    const current = this.getCurrentPassenger();
    const passengers = this.getPassengerSelection().filter((pax) =>
      [ESTATUS_CHECKIN.NOT_DOCUMENTS].includes(pax.estatusCheckin)
    );
    if (passengers.length > 0) {
      if (current) {
        const indxCurrent = passengers.findIndex(
          (pax) => pax.nameRefNumber === current.nameRefNumber
        );
        if (indxCurrent !== -1) {
          const passenger = passengers[indxCurrent - 1];
          this.setPositionCurrentPassenger(indxCurrent - 1);
          this.setSelectCurrentPassenger(passenger);
          return passenger;
        }
        this.setSelectCurrentPassenger(passengers[0]);
        return passengers[0];
      }
      this.setSelectCurrentPassenger(passengers[0]);
      return passengers[0];
    }
    this.setSelectCurrentPassenger(undefined);
    return null;
  }

  getInfantByAdult(passenger: Passenger): Passenger | undefined {
    const passengers = this.getPassengerSelection(true);
    return passengers.find(
      (pax) => pax.nameRefNumber === passenger.passengerInfant && pax.paxType === PAX_TYPE.INFANT
    );
  }

  //#region Bags
  assignBenefitsToPassenger(reservation: Reservation, response: any) {
    // CI-19584: tarifa a nivel de reserva. El método de Carry-On INBURSA solo
    // aplica para MAIN_LIGHT; lo verifica internamente.
    const fareFamily = reservation?.flightSummary?.fareFamily;
    for (let pLoyalty of response) {
      const passenger = reservation.passengers.find(
        (p) => pLoyalty.accountNumber == `00${p.passengerInfo?.ffNumber}`
      );
      if (passenger) {
        passenger.benefits = pLoyalty;
        passenger.typeTier = passenger.benefits?.tierBenefits?.baggage ? 'tier' : null;
        passenger.typeCobrand = passenger.benefits?.cobrandBenefits?.baggage ? 'cobrand' : null;
        passenger.typeff = passenger.benefits?.ffBenefits?.baggage ? 'f&f' : null;
        passenger.typeCorporate = (passenger.corporate as any)?.corporateBenefits?.baggage
          ? 'corporate'
          : null;
        passenger.buildStackBagsBenifit();
        // CI-19584: ruta específica INBURSA Carry-On. Solo aplica si:
        //  - tarifa === MAIN_LIGHT (guarda en el propio método),
        //  - cobrandCode mapea a INBURSA,
        //  - el pax NO tiene Carry-On adquirido aún.
        passenger.buildStackBagsCarryonBenefit(fareFamily);
      }
    }
    this._globalService.setReservation(reservation);
  }

  assignIncludeBagsToPassenger(reservation: Reservation, res: any) {
    const { passengers } = reservation;
    const { baggageIncludes } = res;
    for (let passenger of passengers) {
      const bags = baggageIncludes.find((s) => s.nameRefNumber === passenger.nameRefNumber);
      if (bags) {
        passenger.buildStackBagsInclude(bags.offerCheckBags || []); // Construir el stack de maletas incluidas
        passenger.buildStackBagsCarryOnInclude(bags.offerCarryOn || []); // Construir el stack de maletas de mano incluidas
        // passenger.buildBagsUsedByRemarks(reservation.remarks); // Marcar las maletas usadas por remark TODO cuando se tenga solucion por parte de api revivir esto
      }
    }
    this._globalService.setReservation(reservation);
  }

  getCobrandConfig(passenger: Passenger): CobrandConfig | null {
    const code = passenger.benefits?.cobrandCode;
    if (!code) return null;
    return (cobrandCatalog.cards as CobrandConfig[]).find((c) => c.cobrandCode === code) || null;
  }

  private hasCarryOnAlready(passenger: Passenger): boolean {
    return passenger.bagStackCarryon?.some((bag) => bag.status === StatusBag.PAID) || false;
  }

  /**
   * Abre el modal de canje de beneficios.
   *
   * CI-19584: el parametro target permite enrutar el canje a Carry-On en
   * lugar de checked baggage. Es opcional con default checked para
   * mantener el comportamiento previo en todos los callers existentes.
   *
   * @param passenger pasajero a redimir
   * @param target checked (default) opera sobre bagStack, carryon opera
   *               sobre bagStackCarryon / bagOfferCarryOnStack.
   */
  reedeemAncillaries(
    passenger: Passenger,
    target: 'checked' | 'carryon' = 'checked'
  ): Promise<any> {
    const cobrandConfig = this.getCobrandConfig(passenger);
    const hasCobrand = !!cobrandConfig;

    // Cobrand siempre tiene prioridad
    if (hasCobrand) {
      return this.openCobrandBenefitModal(passenger, cobrandConfig);
    }

    // tier, corporate o sin cobrand: modal existente
    const modalRef = this._modalService.open(BagsRedemptionModalComponent, {
      centered: true,
      size: 'lg',
      backdrop: 'static',
      keyboard: false,
    });
    modalRef.componentInstance.passenger = passenger;
    modalRef.componentInstance.reservation = this._globalService.getReservation();
    modalRef.componentInstance.target = target;
    return modalRef.result;
  }

  /**
   * Deriva el TierBenefitConfig a partir del pasajero.
   * Retorna null si el pasajero no tiene tier ni corporate.
   */
  getTierConfig(passenger: Passenger): TierBenefitConfig | null {
    const tier = passenger?.benefits?.tier;
    const isTier = passenger?.typeTier === 'tier';
    const isCorporate = passenger?.typeCorporate === 'corporate';

    if (!isTier && !isCorporate) return null;

    const method: 'tier' | 'corporate' = isCorporate ? 'corporate' : 'tier';

    const tierKey = tier === 'CA' ? 'CABALLERO AGUILA' : tier;
    const badgeCssClass = (tier === 'CA' ? 'caballero' : classCssTiers[tierKey]) || 'default';

    let badgeLabel: string;
    if (tier === 'CA' || tier === 'CABALLERO AGUILA') {
      badgeLabel = 'Caballero Águila';
    } else if (tier) {
      badgeLabel = tier.charAt(0).toUpperCase() + tier.slice(1).toLowerCase();
    } else {
      badgeLabel = isCorporate ? 'Corporate' : 'Tier';
    }

    return { tierCode: tier || method, method, badgeLabel, badgeCssClass };
  }

  /**
   * Versión unificada de reedeemAncillaries para la vista unified-bag-selection.
   * Cobrand → modal cobrand existente (sin cambios).
   * Tier / Corporate → nuevo modal unificado (BagsBenefitModalComponent con tierConfig).
   */
  reedeemAncillariesUnified(
    passenger: Passenger,
    target: 'checked' | 'carryon' = 'checked'
  ): Promise<any> {
    const cobrandConfig = this.getCobrandConfig(passenger);
    if (cobrandConfig) {
      return this.openCobrandBenefitModal(passenger, cobrandConfig, target);
    }

    const tierConfig = this.getTierConfig(passenger);
    if (tierConfig) {
      return this.openTierBenefitModal(passenger, tierConfig, target);
    }

    return Promise.resolve(ResultModalRedeem.NO_REDEEM);
  }

  private openTierBenefitModal(
    passenger: Passenger,
    tierConfig: TierBenefitConfig,
    target: 'checked' | 'carryon'
  ): Promise<any> {
    const modalRef = this._modalService.open(BagsBenefitModalComponent, {
      centered: true,
      size: 'lg',
      backdrop: 'static',
      keyboard: false,
      animation: false,
      windowClass: 'bags-benefit-modal-window',
    });
    modalRef.componentInstance.tierConfig = tierConfig;

    return modalRef.result
      .then((result) => {
        if (result === ResultModalRedeem.SUCCESS) {
          return this.redeemTierBenefit(passenger, tierConfig.method, target);
        }
        return result;
      })
      .catch(() => ResultModalRedeem.NO_REDEEM);
  }

  private redeemTierBenefit(
    passenger: Passenger,
    method: 'tier' | 'corporate',
    target: 'checked' | 'carryon'
  ): Promise<any> {
    const reservation = this._globalService.getReservation();

    if (target === 'carryon') {
      const offerCarryon = passenger.bagOfferCarryOnStack?.find(
        (b) => b && (b.status === StatusBag.INCLUDE || b.status === StatusBag.NONE)
      );
      const syntheticCarryon = passenger.bagStackCarryon?.find(
        (b) => b.status === StatusBag.REDEEMED
      );
      const ancillary = offerCarryon || syntheticCarryon;
      if (!ancillary) return Promise.resolve(ResultModalRedeem.NO_REDEEM);

      return new Promise((resolve) => {
        this._ancillaries.redeemAncillaries(reservation, passenger, ancillary, method).subscribe({
          next: (res) => {
            try {
              const bagRedeemed = res?.travelerInfo?.[0]?.ancillaries?.[0]?.ancillary;
              let bag = passenger.bagStackCarryon?.find((b) => b.status === StatusBag.REDEEMED);
              if (!bag) {
                bag = { status: StatusBag.REDEEMED } as any;
                passenger.bagStackCarryon = passenger.bagStackCarryon || [];
                passenger.bagStackCarryon.push(bag);
              }
              bag.status = StatusBag.REDEEMED_APPLIED;
              bag.isRedemptionFF = false;
              bag.isRedemptionTier = method === 'tier';
              bag.isRedemptionCobrand = false;
              bag.isRedemptionCorporate = method === 'corporate';
              bag.selected = true;
              if (bagRedeemed) {
                Object.assign(bag, bagRedeemed);
                bag.status = StatusBag.REDEEMED_APPLIED;
              }
              this._globalService.setPassenger(passenger);
            } catch (e) {
              console.error('[redeemTierBenefit carryon] apply state error', e);
            } finally {
              const el = document.activeElement as HTMLElement | null;
              el?.blur?.();
              setTimeout(() => resolve(ResultModalRedeem.SUCCESS), 0);
            }
          },
          error: () => resolve(ResultModalRedeem.ERROR),
        });
      });
    }

    // target === 'checked'
    const extraBags = passenger.bagOfferStack?.find(
      (bag) => bag.order == passenger.bagStack.length
    );

    return new Promise((resolve) => {
      this._ancillaries.redeemAncillaries(reservation, passenger, extraBags, method).subscribe({
        next: (res) => {
          const bag = passenger.bagStack.find((b) => b.status === StatusBag.REDEEMED);
          const bagRedeemed = res?.travelerInfo?.[0]?.ancillaries?.[0]?.ancillary;
          if (bag) {
            bag.status = StatusBag.REDEEMED_APPLIED;
            bag.isRedemptionFF = false;
            bag.isRedemptionTier = method === 'tier';
            bag.isRedemptionCobrand = false;
            bag.isRedemptionCorporate = method === 'corporate';
            bag.selected = true;
            if (bagRedeemed) Object.assign(bag, bagRedeemed, { status: StatusBag.REDEEMED_APPLIED });
          }
          this._globalService.setPassenger(passenger);
          resolve(ResultModalRedeem.SUCCESS);
        },
        error: () => resolve(ResultModalRedeem.ERROR),
      });
    });
  }

  private openCobrandBenefitModal(
    passenger: Passenger,
    cobrandConfig: CobrandConfig,
    target: 'checked' | 'carryon' = 'checked'
  ): Promise<any> {
    const modalRef = this._modalService.open(BagsBenefitModalComponent, {
      centered: true,
      size: 'lg',
      backdrop: 'static',
      keyboard: false,
      animation: false,
      windowClass: 'bags-benefit-modal-window',
    });
    modalRef.componentInstance.cobrandConfig = cobrandConfig;
    modalRef.componentInstance.hasCarryOnAlready = this.hasCarryOnAlready(passenger);

    return modalRef.result
      .then((result) => {
        if (result === ResultModalRedeem.SUCCESS) {
          return this.redeemCobrandBenefit(passenger, cobrandConfig, target);
        }
        return result;
      })
      .catch(() => ResultModalRedeem.NO_REDEEM);
  }

  private redeemCobrandBenefit(
    passenger: Passenger,
    cobrandConfig: CobrandConfig,
    target: 'checked' | 'carryon' = 'checked'
  ): Promise<any> {
    const reservation = this._globalService.getReservation();

    if (target === 'carryon') {
      const ancillary =
        passenger.bagOfferCarryOnStack?.find(
          (b) => b && (b.status === StatusBag.INCLUDE || b.status === StatusBag.NONE)
        ) ||
        passenger.bagStackCarryon?.find((b) => b.status === StatusBag.REDEEMED);

      if (!ancillary) return Promise.resolve(ResultModalRedeem.NO_REDEEM);

      return new Promise((resolve) => {
        this._ancillaries.redeemAncillaries(reservation, passenger, ancillary, 'cobrand').subscribe({
          next: (res) => {
            try {
              const bagRedeemed = res?.travelerInfo?.[0]?.ancillaries?.[0]?.ancillary;
              let bag = passenger.bagStackCarryon?.find((b) => b.status === StatusBag.REDEEMED);
              if (!bag) {
                bag = { status: StatusBag.REDEEMED } as any;
                passenger.bagStackCarryon = passenger.bagStackCarryon || [];
                passenger.bagStackCarryon.push(bag);
              }
              bag.status = StatusBag.REDEEMED_APPLIED;
              bag.isRedemptionCobrand = true;
              bag.selected = true;
              if (bagRedeemed) {
                Object.assign(bag, bagRedeemed);
                bag.status = StatusBag.REDEEMED_APPLIED;
              }
              this._globalService.setPassenger(passenger);
            } catch (e) {
              console.error('[redeemCobrandBenefit carryon] apply state error', e);
            } finally {
              resolve(ResultModalRedeem.SUCCESS);
            }
          },
          error: () => {
            this.openBenefitErrorModal(passenger, cobrandConfig, target).then((errorResult) => {
              resolve(errorResult);
            });
          },
        });
      });
    }

    // target === 'checked': comportamiento original
    const extraBags = passenger.bagOfferStack?.find(
      (bag) => bag.order == passenger.bagStack.length
    );

    return new Promise((resolve) => {
      this._ancillaries.redeemAncillaries(reservation, passenger, extraBags, 'cobrand').subscribe({
        next: (res) => {
          const bag = passenger.bagStack.find((b) => b.status === StatusBag.REDEEMED);
          const bagRedeemed = res?.travelerInfo?.[0]?.ancillaries?.[0]?.ancillary;
          if (bag) {
            bag.status = StatusBag.REDEEMED_APPLIED;
            bag.isRedemptionCobrand = true;
            bag.selected = true;
            if (bagRedeemed)
              Object.assign(bag, bagRedeemed, { status: StatusBag.REDEEMED_APPLIED });
          }
          this._globalService.setPassenger(passenger);
          resolve(ResultModalRedeem.SUCCESS);
        },
        error: () => {
          this.openBenefitErrorModal(passenger, cobrandConfig, target).then((errorResult) => {
            resolve(errorResult);
          });
        },
      });
    });
  }

  private openBenefitErrorModal(
    passenger: Passenger,
    cobrandConfig: CobrandConfig,
    target: 'checked' | 'carryon'
  ): Promise<any> {
    const errorModalRef = this._modalService.open(BagsBenefitErrorModalComponent, {
      centered: true,
      size: 'lg',
      backdrop: 'static',
      keyboard: false,
    });
    errorModalRef.componentInstance.bankNameKey = cobrandConfig.nameKey;

    return errorModalRef.result
      .then((errorResult) => {
        if (errorResult === BENEFIT_ERROR_RETRY) {
          return this.openCobrandBenefitModal(passenger, cobrandConfig, target);
        }
        return ResultModalRedeem.NO_REDEEM;
      })
      .catch(() => ResultModalRedeem.NO_REDEEM);
  }

  setBaggageEligibility(passengers: any[], reservation: Reservation) {
    for (let pax of passengers) {
      const passenger = this.getSelectPassengersToBags()?.find(
        (p) => p.nameRefNumber == pax.passengerNameNumber
      );
      if (passenger) {
        passenger.shortBaggageDrop = pax?.shortBaggageDrop === true;
        for (let ancillary of pax.ancillaries) {
          const bagExist = passenger.bagStack.find(
            (b) => b?.rficSubcode == ancillary?.code && !b.used
          );
          if (bagExist) {
            const segmentCode = reservation?.flightSummary?.currentSegment?.segmentCode;
            bagExist.eligibility =
              ancillary?.eligibility?.find((bag) => segmentCode == bag.segmentCode)
                ?.baggageDropAllowed || false;
            if (
              bagExist.status === StatusBag.INCLUDE ||
              bagExist.status === StatusBag.REDEEMED ||
              bagExist.status === StatusBag.REDEEMED_APPLIED
            ) {
              bagExist.eligibility = true;
            }
          }
        }
      }
    }
    this._globalService.setReservation(reservation);
  }

  allowBaggageWeighing() {
    const hasBagsEligibility = this.getSelectPassengersToBags()?.some((pax) =>
      pax.bagStack.some((bag) => !bag.used && bag.eligibility && bag.selected)
    );
    return hasBagsEligibility;
  }
  //#endregion Bags

  /**
   * Normaliza el valor de género a los valores aceptados por el backend (M/F/U)
   * @param gender Género del pasaporte escaneado
   * @returns 'M' para masculino, 'F' para femenino, 'U' para otros casos
   */
  private normalizeGender(gender: string): string {
    if (!gender) return 'U';

    const g = gender.toUpperCase();

    if (g === 'M' || g === 'MALE') return 'M';
    if (g === 'F' || g === 'FEMALE') return 'F';

    return 'U';
  }

  /*
   * Verifica si alguno de los pasajeros seleccionados no ha verificado sus documentos.
   * @param passengerSelected Lista de pasajeros seleccionados.
   * @returns true si al menos un pasajero no ha verificado sus documentos, false en caso contrario.
   */
  isVeryPassportScannedForPassengers(passengerSelected: Passenger[]): boolean {
    return passengerSelected.some((p) => !p.passengerInfo?.passport?.passportScanned);
  }

  isVerifyPassportRequiredForPassengers(
    passengerSelected: Passenger[],
    isDomestic: boolean
  ): boolean {
    if (isDomestic) return false;
    return this.isVeryPassportScannedForPassengers(passengerSelected);
  }

  isReservationStandby(reservation: Reservation): boolean {
    return reservation.isStandBy || reservation.isStandByBooking;
  }

  isPassengerEligibleForUpgradeList(reservation: Reservation, passenger: Passenger): boolean {
    const employeeEligible =
      reservation.isEmployee && !!passenger?.priorityCode && !!passenger?.upgradeCode;
    const ffEligible = !!passenger?.passengerInfo?.ffNumber && !!passenger?.priorityCode;
    return employeeEligible || ffEligible;
  }

  isStbOrUpgradeListApplicable(reservation: Reservation, passengerSelected: Passenger[]): boolean {
    return (
      this.isReservationStandby(reservation) ||
      passengerSelected.some((p) => this.isPassengerEligibleForUpgradeList(reservation, p))
    );
  }

  shouldShowStbUpgradeListModal(
    reservation: Reservation,
    passengerSelected: Passenger[],
    showModalPriority = true
  ): boolean {
    return this.isStbOrUpgradeListApplicable(reservation, passengerSelected) && showModalPriority;
  }

}
